import React from "react";
import * as Yup from "yup";
import { ErrorMessage, Field, Form, Formik } from "formik";
import classNames from "classnames";
import { Col, Spinner } from "reactstrap";
import PasswordInput from "components/customs/password-input/PasswordInput";
import FormGenerator from "components/customs/form-generator/input";
import CustomSelect from "components/customs/dropdown";
import { useNavigate } from "react-router";
import { addProjectDropdownStyles } from "constants/style";
import { ALL_OPTION } from "utility/constants";
import {
  FormActionsSection,
  FormRow,
  RequiredIndicator,
  StyledCancelButton,
  StyledSaveButton,
} from "assets/js/Styles";
import styled from "styled-components";
import {
  baseValidationSchema,
  emailValidationSchema,
  mobileValidationSchema,
  passwordValidationSchema,
} from "validation";
import { POSITION_AGENCY } from "../constants";

const validSchema = baseValidationSchema
  .concat(emailValidationSchema)
  .concat(mobileValidationSchema)
  .concat(passwordValidationSchema)
  .shape({
    agency: Yup.object().required("Required"),
    programs: Yup.array()
      .of(
        Yup.object().shape({
          label: Yup.string().required(),
          value: Yup.string().required(),
        })
      )
      .min(1, "At least one program must be selected")
      .required("Required"),
  });

const LastNameFiledContainer = styled(FormRow)`
  margin-top: 0;
`;

const AgencyForm = ({
  handleOnSubmit,
  formFields,
  setFormFields,
  agency,
  agencies,
  selectedAgency,
  setSelectedAgency,
  isLoading,
  programOptions,
}) => {
  const navigate = useNavigate();

  return (
    <Formik
      validationSchema={validSchema}
      initialValues={formFields}
      enableReinitialize
      onSubmit={handleOnSubmit}
    >
      {({ handleChange, values }) => (
        <Form>
          <Field name="lastname">
            {({ form: { touched, errors } }) => (
              <LastNameFiledContainer>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>
                    {"Last Name"}
                    {RequiredIndicator}
                  </h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <FormGenerator
                    type="text"
                    name="lastname"
                    className={classNames({
                      "is-invalid": errors?.lastname && touched?.lastname,
                    })}
                    value={values.lastname}
                    onChange={(evt) => {
                      handleChange(evt);

                      setFormFields({
                        ...formFields,
                        lastname: evt.target.value || "",
                      });
                    }}
                  />

                  <ErrorMessage name="lastname">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </LastNameFiledContainer>
            )}
          </Field>

          <Field name="firstname">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>
                    {"First Name"}
                    {RequiredIndicator}
                  </h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <FormGenerator
                    type="text"
                    name="firstname"
                    className={classNames({
                      "is-invalid": errors?.firstname && touched?.firstname,
                    })}
                    value={values.firstname}
                    onChange={(evt) => {
                      handleChange(evt);

                      setFormFields({
                        ...formFields,
                        firstname: evt.target.value || "",
                      });
                    }}
                  />
                  <ErrorMessage name="firstname">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="middleInitial">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>{"Middle Name"}</h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <FormGenerator
                    type="text"
                    name="middleInitial"
                    className={classNames({
                      "is-invalid":
                        errors?.middleInitial && touched?.middleInitial,
                    })}
                    value={values.middleInitial}
                    onChange={(evt) => {
                      handleChange(evt);

                      setFormFields({
                        ...formFields,
                        middleInitial: evt.target.value || "",
                      });
                    }}
                  />
                  <ErrorMessage name="middleInitial">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="email">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>
                    {"Email"}
                    {RequiredIndicator}
                  </h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <FormGenerator
                    type="text"
                    name="email"
                    className={classNames({
                      "is-invalid": errors?.email && touched?.email,
                    })}
                    value={values.email}
                    onChange={(evt) => {
                      handleChange(evt);

                      setFormFields({
                        ...formFields,
                        email: evt.target.value || "",
                      });
                    }}
                  />
                  <ErrorMessage name="email">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          {!agency && (
            <>
              <Field name="password">
                {({ form: { touched, errors } }) => (
                  <FormRow>
                    <Col
                      xs="12"
                      sm="6"
                      md="12"
                      lg="6"
                    >
                      <h5>
                        {"Password"}
                        {RequiredIndicator}
                      </h5>
                    </Col>
                    <Col
                      xs="12"
                      sm="6"
                      md="12"
                      lg="6"
                    >
                      <PasswordInput
                        type="password"
                        name="password"
                        className={classNames({
                          "is-invalid": errors?.password && touched?.password,
                        })}
                        onChange={(evt) => {
                          handleChange(evt);

                          setFormFields({
                            ...formFields,
                            password: evt.target.value || "",
                          });
                        }}
                      />
                      <ErrorMessage name="password">
                        {(msg) => (
                          <small className="field-error text-danger">
                            {msg}
                          </small>
                        )}
                      </ErrorMessage>
                    </Col>
                  </FormRow>
                )}
              </Field>

              <Field name="passwordConfirmation">
                {({ form: { touched, errors } }) => (
                  <FormRow>
                    <Col
                      xs="12"
                      sm="6"
                      md="12"
                      lg="6"
                    >
                      <h5>
                        {"Confirm Password"}
                        {RequiredIndicator}
                      </h5>
                    </Col>
                    <Col
                      xs="12"
                      sm="6"
                      md="12"
                      lg="6"
                    >
                      <PasswordInput
                        type="password"
                        name="passwordConfirmation"
                        className={classNames({
                          "is-invalid":
                            errors?.passwordConfirmation &&
                            touched?.passwordConfirmation,
                        })}
                        onChange={(evt) => {
                          handleChange(evt);

                          setFormFields({
                            ...formFields,
                            passwordConfirmation: evt.target.value || "",
                          });
                        }}
                      />
                      <ErrorMessage name="passwordConfirmation">
                        {(msg) => (
                          <small className="field-error text-danger">
                            {msg}
                          </small>
                        )}
                      </ErrorMessage>
                    </Col>
                  </FormRow>
                )}
              </Field>
            </>
          )}

          <Field name="mobile">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>
                    {"Contact Number"}
                    {RequiredIndicator}
                  </h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <FormGenerator
                    type="mobile"
                    name="mobile"
                    className={classNames({
                      "is-invalid": errors?.mobile && touched?.mobile,
                    })}
                    value={values.mobile || ""}
                    onChange={(value) => {
                      handleChange(value);

                      setFormFields({
                        ...formFields,
                        mobile: value || "",
                      });
                    }}
                  />
                  <ErrorMessage name="mobile">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="agency">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>
                    {"Agency"}
                    {RequiredIndicator}
                  </h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <CustomSelect
                    placeholder=""
                    name="agency"
                    styles={addProjectDropdownStyles}
                    options={agencies}
                    value={values.agency}
                    className={classNames({
                      "is-invalid": errors?.agency && touched?.agency,
                    })}
                    onChange={(option) => {
                      setSelectedAgency(option);

                      setFormFields({
                        ...formFields,
                        agency: option,
                      });
                    }}
                    filterOption={(option, searchValue) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(searchValue?.toLowerCase()) ||
                      option?.data?.acronym
                        ?.toLowerCase()
                        ?.includes(searchValue?.toLowerCase())
                    }
                  />
                  <ErrorMessage name="agency">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="programs">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>
                    {"Programs"}
                    {RequiredIndicator}
                  </h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <CustomSelect
                    placeholder=""
                    name="programs"
                    isMulti
                    isDisabled={!selectedAgency}
                    styles={addProjectDropdownStyles}
                    className={classNames({
                      "is-invalid": errors?.programs && touched?.programs,
                    })}
                    options={
                      values.programs?.some(
                        (program) => program.value === ALL_OPTION
                      )
                        ? []
                        : programOptions
                    }
                    value={values.programs}
                    onChange={(options) => {
                      const ops = options?.map(({ label, value }) => ({
                        label,
                        value,
                      }));

                      setFormFields({
                        ...formFields,
                        programs: ops,
                      });
                    }}
                  />
                  <ErrorMessage name="programs">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="cosLastName">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>{"Chief of Staff Last Name"}</h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <FormGenerator
                    type="text"
                    name="cosLastName"
                    className={classNames({
                      "is-invalid": errors?.cosLastName && touched?.cosLastName,
                    })}
                    value={values.cosLastName}
                    onChange={(evt) => {
                      handleChange(evt);

                      setFormFields({
                        ...formFields,
                        cosLastName: evt.target.value || "",
                      });
                    }}
                  />
                  <ErrorMessage name="cosLastName">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="cosFirstName">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>{"Chief of Staff First Name"}</h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <FormGenerator
                    type="text"
                    name="cosFirstName"
                    className={classNames({
                      "is-invalid":
                        errors?.cosFirstName && touched?.cosFirstName,
                    })}
                    value={values.cosFirstName}
                    onChange={(evt) => {
                      handleChange(evt);

                      setFormFields({
                        ...formFields,
                        cosFirstName: evt.target.value || "",
                      });
                    }}
                  />
                  <ErrorMessage name="cosFirstName">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="cosNumber">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>{"Contact Number"}</h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <FormGenerator
                    type="mobile"
                    name="cosNumber"
                    className={classNames({
                      "is-invalid": errors?.cosNumber && touched?.cosNumber,
                    })}
                    value={values.cosNumber || ""}
                    onChange={(value) => {
                      handleChange(value);

                      setFormFields({
                        ...formFields,
                        cosNumber: value || "",
                      });
                    }}
                  />
                  <ErrorMessage name="cosNumber">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <FormActionsSection>
            <StyledCancelButton
              outline
              disabled={isLoading}
              onClick={() =>
                navigate("/directory", {
                  state: { view: POSITION_AGENCY },
                })
              }
            >
              {"Cancel"}
            </StyledCancelButton>

            <StyledSaveButton
              color="primary"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <Spinner
                  animation="border"
                  color="light"
                  size="sm"
                />
              ) : (
                "Save"
              )}
            </StyledSaveButton>
          </FormActionsSection>
        </Form>
      )}
    </Formik>
  );
};

export default AgencyForm;
